import { useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { AlarmClock, Download, FileText, Receipt, Truck, Wallet } from 'lucide-react';
import { INVOICE_STATUS, TALLY_STATUS, dateLabel } from '@adisys/shared';
import { api, downloadCsv } from '../../lib/api';
import { useAuth } from '../../lib/auth';
import { Kpi, KpiSkeleton, MetaBadge, StageBadge, inr, inr2, qtyLabel } from '../../lib/crm';
import { PageHeader } from '../../components/AppShell';
import { Column, DataTable, FilterBar, FilterSelect, SearchInput } from '../../components/DataTable';
import { Button, Card, CardHeader, Tabs, cx, useToast } from '../../components/ui';
import { DispatchModal } from './finance/DispatchModal';
import { InvoiceModal } from './finance/InvoiceModal';

type TabKey = 'invoices' | 'orders' | 'receivables';

export function FinancePage() {
  const { can } = useAuth();
  const toast = useToast();
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();
  const tab = (['invoices', 'orders', 'receivables'].includes(params.get('tab') ?? '') ? params.get('tab') : 'invoices') as TabKey;
  const setTab = (t: TabKey) => setParams({ tab: t }, { replace: true });

  const summary = useQuery({ queryKey: ['crm', 'finance', 'summary'], queryFn: () => api.get('/crm/finance/summary') });
  const s = summary.data;
  const buckets = s ? [
    { label: '1–30 days', v: Number(s.age1to30) }, { label: '31–60', v: Number(s.age31to60) },
    { label: '61–90', v: Number(s.age61to90) }, { label: '90+', v: Number(s.age90plus) },
  ] : [];
  const maxBucket = Math.max(1, ...buckets.map((b) => b.v));

  const [dispatchFor, setDispatchFor] = useState<string | null>(null);
  const [invoiceFor, setInvoiceFor] = useState<string | null>(null);

  return (
    <>
      <PageHeader title="Invoices & payments"
        description="Dispatch what is ready, bill what has been delivered, and chase what is outstanding." />
      <div className="space-y-5 p-4 sm:p-6">
        {summary.isLoading ? (
          <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">{Array.from({ length: 4 }).map((_, i) => <KpiSkeleton key={i} />)}</div>
        ) : s && (
          <>
            <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
              <Kpi label="Billed" value={inr(s.billed, true)} icon={Receipt} sub={`${inr(s.collectedThisMonth, true)} collected this month`} />
              <Kpi label="Collected" value={inr(s.collected, true)} icon={Wallet} tone="success" />
              <Kpi label="Outstanding" value={inr(s.outstanding, true)} icon={Wallet}
                sub={`${inr(s.dueThisWeek, true)} due within a week`} />
              <Kpi label="Overdue" value={inr(s.overdue, true)} icon={AlarmClock} tone={Number(s.overdue) > 0 ? 'danger' : 'neutral'}
                sub={Number(s.overdue) > 0 ? 'Needs follow-up' : 'Nothing overdue'} />
            </div>
            <div className="grid gap-3 lg:grid-cols-3">
              <Card className="lg:col-span-2">
                <CardHeader title="Overdue ageing" subtitle="Amount past its due date, by how long." />
                <div className="space-y-2">
                  {buckets.map((b) => (
                    <div key={b.label} className="flex items-center gap-3 text-xs">
                      <span className="w-20 shrink-0 text-ink-500">{b.label}</span>
                      <span className="h-2.5 flex-1 overflow-hidden rounded-full bg-ink-100">
                        <span className="block h-full rounded-full bg-danger" style={{ width: `${(b.v / maxBucket) * 100}%` }} />
                      </span>
                      <span className="tabular w-24 text-right text-ink-800">{inr(b.v, true)}</span>
                    </div>
                  ))}
                </div>
              </Card>
              <Card>
                <CardHeader title="Tally" subtitle={s.tallyConfigured ? 'Endpoint configured' : 'No endpoint configured; vouchers stay queued'} />
                <dl className="space-y-1.5 text-sm">
                  <div className="flex justify-between"><dt className="text-ink-500">Pending sync</dt><dd className="tabular font-medium">{s.tallyPending}</dd></div>
                  <div className="flex justify-between"><dt className="text-ink-500">Failed</dt>
                    <dd className={cx('tabular font-medium', s.tallyFailed > 0 && 'text-danger')}>{s.tallyFailed}</dd></div>
                </dl>
              </Card>
            </div>
          </>
        )}

        <Tabs<TabKey> active={tab} onChange={setTab}
          tabs={[{ key: 'invoices', label: 'Invoices' }, { key: 'orders', label: 'Ready to dispatch / bill' }, { key: 'receivables', label: 'Receivables' }]} />

        {tab === 'invoices' && <InvoicesTable onOpen={(id) => navigate(`/crm/finance/invoices/${id}`)} />}
        {tab === 'receivables' && (
          <InvoicesTable unpaidOnly onOpen={(id) => navigate(`/crm/finance/invoices/${id}`)}
            extra={can('crm.report.view') && (
              <Button icon={<Download className="h-4 w-4" />}
                onClick={() => downloadCsv('/crm/reports/receivables', {}, 'crm-receivables.csv').catch(() => toast.error('Export failed'))}>
                Export CSV
              </Button>)} />
        )}
        {tab === 'orders' && <OrdersTable canManage={can('crm.finance.manage')} onDispatch={setDispatchFor} onInvoice={setInvoiceFor} />}
      </div>

      <DispatchModal open={!!dispatchFor} orderId={dispatchFor} onClose={() => setDispatchFor(null)} />
      <InvoiceModal open={!!invoiceFor} orderId={invoiceFor} onClose={() => setInvoiceFor(null)} />
    </>
  );
}

function InvoicesTable({ unpaidOnly, onOpen, extra }: { unpaidOnly?: boolean; onOpen: (id: string) => void; extra?: React.ReactNode }) {
  const [q, setQ] = useState('');
  const [status, setStatus] = useState('');
  const [tally, setTally] = useState('');
  const [overdue, setOverdue] = useState('');
  const [page, setPage] = useState(1);
  const query = useQuery({
    queryKey: ['crm', 'invoices', { q, status, tally, overdue, page, unpaidOnly }],
    queryFn: () => api.get('/crm/invoices', {
      q: q || undefined, status: status || undefined, tally: tally || undefined, overdue: overdue || undefined,
      unpaid: unpaidOnly ? 'true' : undefined, page, size: 25,
    }),
  });
  const rows: any[] = query.data?.data ?? [];
  const sorted = unpaidOnly ? [...rows].sort((a, b) => String(a.dueDate).localeCompare(String(b.dueDate))) : rows;

  const columns: Array<Column<any>> = [
    { key: 'no', header: 'Invoice', render: (i) => (<div><p className="font-medium text-ink-900">{i.invoiceNo}</p>
        <p className="text-xs text-ink-500">{i.projectCode}</p></div>) },
    { key: 'customer', header: 'Customer', render: (i) => <span className="text-ink-800">{i.customerName}</span> },
    { key: 'date', header: 'Dated', hideBelow: 'lg', render: (i) => dateLabel(i.invoiceDate) },
    { key: 'due', header: 'Due', hideBelow: 'md', render: (i) => (
        <span className={cx(i.isOverdue && 'font-medium text-danger')}>{dateLabel(i.dueDate)}
          {i.isOverdue && <span className="block text-xs">{i.daysOverdue} days late</span>}</span>) },
    { key: 'total', header: 'Total', numeric: true, render: (i) => inr2(i.grandTotal) },
    { key: 'paid', header: 'Paid', numeric: true, hideBelow: 'xl', render: (i) => inr2(i.paidAmount) },
    { key: 'out', header: 'Outstanding', numeric: true, render: (i) => <span className="font-medium text-ink-900">{inr2(i.outstanding)}</span> },
    { key: 'tally', header: 'Tally', hideBelow: 'lg', render: (i) => <MetaBadge meta={TALLY_STATUS} value={i.tallyStatus} /> },
    { key: 'status', header: 'Status', render: (i) => <MetaBadge meta={INVOICE_STATUS} value={i.status} /> },
  ];

  return (
    <DataTable mobile="cards" columns={columns} rows={sorted} rowKey={(i) => i.id} onRowClick={(i) => onOpen(i.id)}
      loading={query.isLoading} error={query.error} onRetry={() => query.refetch()}
      page={query.data?.page} onPage={setPage}
      empty={{ icon: <FileText className="h-5 w-5" />, title: unpaidOnly ? 'Nothing outstanding' : 'No invoices yet',
        description: 'Invoices appear here once goods are delivered and billed.' }}
      toolbar={
        <FilterBar active={[q, status, tally, overdue].filter(Boolean).length}
          onReset={() => { setQ(''); setStatus(''); setTally(''); setOverdue(''); setPage(1); }}>
          <SearchInput value={q} onChange={(v) => { setQ(v); setPage(1); }} placeholder="Invoice, project or customer" />
          {!unpaidOnly && <FilterSelect label="Status" value={status} onChange={(v) => { setStatus(v); setPage(1); }} allLabel="All statuses"
            options={INVOICE_STATUS.list.map((s) => ({ value: s.value, label: s.label }))} />}
          <FilterSelect label="Tally" value={tally} onChange={(v) => { setTally(v); setPage(1); }} allLabel="Any Tally state"
            options={TALLY_STATUS.list.map((s) => ({ value: s.value, label: s.label }))} />
          <FilterSelect label="Overdue" value={overdue} onChange={(v) => { setOverdue(v); setPage(1); }} allLabel="Overdue or not"
            options={[{ value: 'true', label: 'Overdue only' }]} />
          {extra && <div className="ml-auto">{extra}</div>}
        </FilterBar>}
    />
  );
}

function OrdersTable({ canManage, onDispatch, onInvoice }: {
  canManage: boolean; onDispatch: (id: string) => void; onInvoice: (id: string) => void;
}) {
  const [q, setQ] = useState('');
  const [page, setPage] = useState(1);
  const query = useQuery({
    queryKey: ['crm', 'finance', 'orders', { q, page }],
    queryFn: () => api.get('/crm/finance/orders', { q: q || undefined, page, size: 25 }),
  });
  const columns: Array<Column<any>> = [
    { key: 'order', header: 'Order', render: (o) => (<div><Link className="font-medium text-ink-900 hover:underline" to={`/crm/projects/${o.projectId}?tab=finance`}
        onClick={(e) => e.stopPropagation()}>{o.orderNo}</Link><p className="text-xs text-ink-500">{o.projectName} · {o.customerName}</p></div>) },
    { key: 'stage', header: 'Stage', hideBelow: 'md', render: (o) => <StageBadge stage={o.projectStage} /> },
    { key: 'ready', header: 'Ready to dispatch', numeric: true, render: (o) => qtyLabel(o.readyToDispatch) },
    { key: 'deliv', header: 'Delivered', numeric: true, hideBelow: 'md', render: (o) => `${qtyLabel(o.delivered)} / ${qtyLabel(o.units)}` },
    { key: 'unbilled', header: 'Delivered, not billed', numeric: true, hideBelow: 'lg', render: (o) => qtyLabel(o.deliveredNotBilled) },
    { key: 'billed', header: 'Billed / collected', numeric: true, hideBelow: 'xl', render: (o) => `${inr(o.billed, true)} / ${inr(o.collected, true)}` },
    { key: 'act', header: '', render: (o) => canManage && (
        <div className="flex justify-end gap-1.5" onClick={(e) => e.stopPropagation()}>
          <Button size="sm" icon={<Truck className="h-3.5 w-3.5" />} disabled={Number(o.readyToDispatch) <= 0} onClick={() => onDispatch(o.id)}>Dispatch</Button>
          <Button size="sm" icon={<Receipt className="h-3.5 w-3.5" />}
            disabled={Number(o.deliveredNotBilled) + Number(o.servicesNotBilled) <= 0} onClick={() => onInvoice(o.id)}>Invoice</Button>
        </div>) },
  ];
  return (
    <DataTable mobile="cards" columns={columns} rows={query.data?.data ?? []} rowKey={(o) => o.id}
      loading={query.isLoading} error={query.error} onRetry={() => query.refetch()} page={query.data?.page} onPage={setPage}
      empty={{ icon: <Truck className="h-5 w-5" />, title: 'No approved orders yet', description: 'Orders appear once a customer approves a quotation.' }}
      toolbar={<FilterBar><SearchInput value={q} onChange={(v) => { setQ(v); setPage(1); }} placeholder="Order, project or customer" /></FilterBar>} />
  );
}
